import type { ReactNode } from "react";
import { FileText, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  formatFileSize,
  formatQuantityDifference,
  getReceivedDifference,
  groupItemsBySeries,
  ITEM_REQUEST_VARIANT_COPY,
  type ItemRequestStep,
  type ItemRequestVariant,
  type SelectableOrderItem,
} from "./supportRequest";

const ReviewSection = ({
  title,
  editLabel,
  onEdit,
  disabled,
  children,
}: {
  title: string;
  editLabel: string;
  onEdit: () => void;
  disabled?: boolean;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-2.5 rounded-[10px] border px-4 py-3.5">
    <div className="flex items-center justify-between gap-4">
      <h4 className="text-[13px] font-bold text-muted-foreground">{title}</h4>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onEdit}
        disabled={disabled}
        aria-label={editLabel}
        className="h-8 gap-1.5 px-2 text-primary hover:text-primary"
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        Editar
      </Button>
    </div>
    {children}
  </section>
);

/** Quantidade de cada item no resumo: a pedida na troca, ou pedido x recebido com a diferença na conferência. */
const ItemQuantitySummary = ({
  item,
  variant,
  raw,
}: {
  item: SelectableOrderItem;
  variant: ItemRequestVariant;
  raw: string;
}) => {
  if (variant === "conference") {
    const difference = getReceivedDifference(item, raw);
    return (
      <span className="flex shrink-0 items-center gap-2 text-[13px] text-muted-foreground">
        Pedido {item.quantityOrdered} · Recebido {raw.trim()}
        {difference !== null && difference !== 0 && (
          <span className="rounded-md bg-amber-100 px-1.5 py-0.5 font-bold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            {formatQuantityDifference(difference)}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="shrink-0 text-[13px] text-muted-foreground">
      Qtd. {raw.trim()} de {item.quantityOrdered}
    </span>
  );
};

interface ItemRequestReviewProps {
  variant: ItemRequestVariant;
  /** Itens marcados, na ordem da lista. */
  items: SelectableOrderItem[];
  quantities: Record<string, string>;
  description: string;
  attachments: File[];
  onEdit: (step: ItemRequestStep) => void;
  disabled?: boolean;
}

/** Último passo: o que vai no chamado (itens por série, descrição e anexos), com atalho para editar cada parte. */
export const ItemRequestReview = ({
  variant,
  items,
  quantities,
  description,
  attachments,
  onEdit,
  disabled,
}: ItemRequestReviewProps) => {
  const copy = ITEM_REQUEST_VARIANT_COPY[variant];
  const trimmedDescription = description.trim();

  return (
    <div className="flex flex-col gap-3">
      <ReviewSection title="Produtos" editLabel="Editar produtos" onEdit={() => onEdit("items")} disabled={disabled}>
        <div className="flex flex-col gap-3">
          {groupItemsBySeries(items).map((group) => (
            <div key={group.key} className="flex flex-col gap-1.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.key}</p>
              <ul className="flex flex-col gap-1.5" aria-label={`Produtos de ${group.key}`}>
                {group.items.map((item) => (
                  <li key={item.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5">
                    <span className="min-w-0 text-[14.5px] font-semibold leading-snug">{item.productDescription}</span>
                    <ItemQuantitySummary item={item} variant={variant} raw={quantities[item.id] ?? ""} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </ReviewSection>

      <ReviewSection title="Detalhes" editLabel="Editar detalhes" onEdit={() => onEdit("details")} disabled={disabled}>
        <div className="flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground">{copy.descriptionLabel}</p>
          <p className="whitespace-pre-wrap break-words text-[14.5px]">
            {trimmedDescription || <span className="text-muted-foreground">Sem observação.</span>}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-xs font-semibold text-muted-foreground">Anexos</p>
          {attachments.length === 0 ? (
            <p className="text-[14.5px] text-muted-foreground">Nenhum anexo.</p>
          ) : (
            <ul className="flex flex-col gap-1" aria-label="Anexos da solicitação">
              {attachments.map((file) => (
                <li key={`${file.name}-${file.size}-${file.lastModified}`} className="flex items-center gap-2 text-[13.5px]">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">{file.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </ReviewSection>
    </div>
  );
};
